import jeddaiIconIcon from '../../static/img/JeddAI-Icon-active.svg';
import taskIcon from '../../static/img/Task2.svg';
import activeClassIcon from '../../static/img/active-class-insights-icon.svg';
import activesettingIcon from '../../static/img/activesetting.svg';
import activetaskIcon from '../../static/img/activetask.svg';
import classIcon from '../../static/img/insights.svg';
import jeddaiIcon from '../../static/img/jeddaigray32.svg';
import settingIcon from '../../static/img/setting.svg';
import getfeedbackIcon from '../../static/img/getfeedback32gray.svg';
import activeGetfeedbackIcon from '../../static/img/activeGetFeedback.svg';
import { checkIsActive } from '../../components/MainSidebar/rules';
import { toProductionPath } from '../utils/routeAliases';

// The mirror sidebar only renders during onboarding (via SidebarSwitcher in
// App.jsx). Nav items mirror the production sidebar's role gating so the demo
// left nav matches the user's actual role: students see a student nav (My
// Drafts + School Work) and never the teacher-only Class Insights / Marking
// Tools. The demo feedback CONTENT stays teacher-view; only this left nav
// reflects the role.
//
// Extracted from index.jsx so the pure nav-building logic is unit-testable
// without pulling in the sidebar's heavy component dependencies.
export const buildNavItems = (location, role, isSchoolAccount, isChecklistOpen = false) => {
  const isStudent = String(role).toUpperCase() === 'STUDENT';
  // Mirror routes never match the production path lists below; translate
  // /onboarding/* to the production family it mirrors so the same
  // checkIsActive rules light the correct item during the tours + wizard.
  // While the setup checklist is the visible screen no route item may read
  // as selected (the Setup Progress item is the selected one) — feed the
  // matchers a pathname that can never match.
  const effectiveLocation = {
    pathname: isChecklistOpen ? '' : toProductionPath(location?.pathname || ''),
  };
  const items = [
    {
      icon: isStudent ? getfeedbackIcon : jeddaiIcon,
      activeIcon: isStudent ? activeGetfeedbackIcon : jeddaiIconIcon,
      isActive: checkIsActive(effectiveLocation, ['/getFeedback', '/documents', '/drafts', '/view/submissions']),
      name: 'My Drafts',
      link: '/getFeedback',
      iconSize: 48,
      needsActiveBackground: false,
    },
  ];

  // Tasks (teacher) / "School Work" (student) is a school-account surface — it
  // lists class-assigned work, which individual (non-school) accounts don't
  // have. Production hides it for them via permissions; the mirror was always
  // showing it, so individual students saw a "School Work" icon (JED-1101).
  // Gate it on the school-account signal (profile.permissions.hasClasses).
  if (isSchoolAccount) {
    items.push({
      icon: taskIcon,
      activeIcon: activetaskIcon,
      isActive: checkIsActive(effectiveLocation, [
        '/',
        '/tasks',
        '/completed',
        '/submissions/',
        '/giveFeedback',
        '/feedbackHistory',
        '/sharedresponses',
        '/feedbackRequest/documentsReview',
        '/feedbackHistory/documentsReview',
        '/submissions',
        '/sharedtasks',
      ]),
      // Production sidebar renames Tasks -> "School Work" for students.
      name: isStudent ? 'School Work' : 'Tasks',
      link: '/tasks',
      iconSize: 48,
      needsActiveBackground: false,
    });
  }

  // Class Insights + Marking Tools are teacher-only (the production sidebar
  // gates Marking Tools on jeddaiRole !== 'STUDENT'). Students never see them.
  if (!isStudent) {
    items.push(
      {
        icon: classIcon,
        activeIcon: activeClassIcon,
        isActive: checkIsActive(effectiveLocation, ['/classes-insights', '/class-insights', '/classes']),
        name: 'Class Insights',
        link: '/classes-insights',
        iconSize: 48,
        needsActiveBackground: false,
      },
      {
        icon: settingIcon,
        activeIcon: activesettingIcon,
        name: 'Marking Tools',
        isActive: checkIsActive(effectiveLocation, [
          '/tools/markingcriterias',
          '/tools/rubrics',
          '/tools/commentbanks',
          '/tools/successcriterias',
          '/tools/shortanswers',
          '/jeddai',
          '/sharedmarkingtools',
          '/advanced-marking',
          '/tools/advanced-controls',
        ]),
        // Mirror sidebar is never a tour entry — link goes to the production
        // page. If the user is mid-tour, TourGuard bounces back to /onboarding/*.
        // The tour into the mirror Marking page is started from OnboardingComplete.
        link: '/tools/rubrics',
        iconSize: 48,
        needsActiveBackground: false,
      }
    );
  }

  return items;
};
